import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { Update } from '@ngrx/entity';

import * as TournamentSummariesActions from './tournament-summaries.actions';
import * as AuthActions from '@app/store/features/auth/auth.actions';
import { TournamentSummary } from '@app/models/tournament-summary';
import { TournamentStatus2 } from '@app/enums/tournament-status-2';

export interface State extends EntityState<TournamentSummary> {
    loader: boolean;
}

export const adapter: EntityAdapter<TournamentSummary> = createEntityAdapter<TournamentSummary>({
    selectId: (model: TournamentSummary) => model.id,
    sortComparer: tournamentSort,
});

export const initialState: State = adapter.getInitialState({
    loader: false,
});

const STATUS_PRIORITY: Record<number, number> = {
    [TournamentStatus2.RunningRegistrationClosed]: 1,
    [TournamentStatus2.RunningWithLateRegistration]: 2,
    [TournamentStatus2.RegistrationOpen]: 3,
    [TournamentStatus2.Announced]: 4,
    [TournamentStatus2.Completed]: 5,
    [TournamentStatus2.Canceled]: 6,
};

function statusPriority(status2: number): number {
    return STATUS_PRIORITY[status2] ?? Number.MAX_SAFE_INTEGER;
}

// Secondary sort key within a status group (ascending => countdown closest to 0 first)
function secondarySortDate(tournament: TournamentSummary): number {
    let date: Date | undefined;
    switch (tournament.status2) {
        case TournamentStatus2.RunningWithLateRegistration:
            date = tournament.game?.lateRegistrationStartInDate; // time left until late registration closes
            break;
        case TournamentStatus2.Announced:
            date = tournament.game?.registrationsStartInDate; // time left until registration opens
            break;
        default:
            date = tournament.game?.startInDate; // starting time
            break;
    }
    return date?.getTime() ?? Number.MAX_SAFE_INTEGER;
}

export function tournamentSort(propA: TournamentSummary, propB: TournamentSummary): number {
    // 1. Sort by status priority
    const priorityDiff = statusPriority(propA.status2) - statusPriority(propB.status2);
    if (priorityDiff !== 0) {
        return priorityDiff;
    }
    // 2. Within the same status, sort by the status-specific date (ascending)
    const dateDiff = secondarySortDate(propA) - secondarySortDate(propB);
    if (dateDiff !== 0) {
        return dateDiff;
    }
    // 3. Tie-break by name (ascending)
    return propA.name.localeCompare(propB.name);
}

export const reducer = createReducer(initialState,
    on(TournamentSummariesActions.updateLoader, (state, { loader }) => {
        return { ...state, loader };
    }),

    on(TournamentSummariesActions.updateAll, (state, { tournamentSummaries }) => {
        return adapter.setAll(tournamentSummaries, { ...state, loader: false });
    }),

    on(TournamentSummariesActions.updateOne, (state, { id, tournamentSummary }) => {
        return adapter.updateOne({ id, changes: { ...tournamentSummary } }, { ...state });
    }),

    on(AuthActions.onLogout, (state) => {
        return adapter.removeAll({ ...state });
    }),

    // on(TournamentSummariesActions.updateTournamentCountDown, (state) => {
    //     return adapter.updateMany({ id: [], changes: { game.scheduleDemo = 123 } });
    // }),
);
